import { useEffect, useState, useRef, useLayoutEffect } from 'react'
import type { Category } from '@/types'
import { getTranslation } from '@/lib/i18n'
import type { Language } from '@/types'

interface CategoryTabsProps {
  categories: Category[]
  language: Language
  isRTL: boolean
  activeCategory?: string
  onCategoryChange?: (categoryId: string) => void
}

/**
 * Scrolls to a category section, accounting for the fixed header + sticky toolbar.
 */
function scrollToCategory(categoryId: string, offset: number) {
  const el = document.getElementById(`category-section-${categoryId}`)
  if (!el) return

  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top, behavior: 'smooth' })
}

export function CategoryTabs({
  categories,
  language,
  isRTL,
  activeCategory,
  onCategoryChange,
}: CategoryTabsProps) {
  // Offset = header (64/80) + search bar (~60) + this tabs block (~48) + padding
  const [scrollOffset, setScrollOffset] = useState(180)
  const [overflowing, setOverflowing] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const update = () => {
      const headerH = window.innerWidth >= 640 ? 80 : 64
      setScrollOffset(headerH + 60 + 48 + 8)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Detect whether the pill row overflows (controls the edge-fade affordances)
  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current
      if (!el) return
      setOverflowing(el.scrollWidth - el.clientWidth > 2)
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (scrollRef.current) ro.observe(scrollRef.current)
    return () => ro.disconnect()
  }, [categories, language])

  // Auto-center the active pill whenever it changes (scroll-spy or tap)
  useEffect(() => {
    if (!activeCategory || !scrollRef.current) return
    const pill = scrollRef.current.querySelector(
      `[data-cat-id="${activeCategory}"]`
    ) as HTMLElement | null
    // block:'nearest' avoids any vertical page jump; inline:'center' slides it into view
    pill?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [activeCategory])

  const handleClick = (categoryId: string) => {
    onCategoryChange?.(categoryId)
    scrollToCategory(categoryId, scrollOffset)
  }

  // Track active category by scroll position (highlight current section)
  useEffect(() => {
    const handleScroll = () => {
      const headerH = window.innerWidth >= 640 ? 80 : 64
      const probe = headerH + 120
      for (const cat of categories) {
        const el = document.getElementById(`category-section-${cat.id}`)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top <= probe && rect.bottom > probe) {
          if (cat.id !== activeCategory) {
            onCategoryChange?.(cat.id)
          }
          break
        }
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [categories, activeCategory, onCategoryChange])

  const flatCategories = categories.map((category) => {
    const itemCount = category.subcategories.reduce(
      (sum, sub) => sum + sub.items.filter((item) => item.available).length,
      0
    )
    return {
      id: category.id,
      name: getTranslation(
        { fr: category.name_fr, ar: category.name_ar, en: category.name_en },
        language
      ),
      itemCount,
    }
  })

  return (
    <nav
      className="bg-background/95 backdrop-blur-sm border-b border-secondary/20"
      dir={isRTL ? 'rtl' : 'ltr'}
      aria-label="Menu categories"
    >
      <div className="relative max-w-7xl mx-auto">
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide"
          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        >
          <div className={`flex gap-2 px-3 sm:px-4 py-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {flatCategories.map((category) => {
              const isActive = activeCategory === category.id
              return (
                <button
                  key={category.id}
                  data-cat-id={category.id}
                  onClick={() => handleClick(category.id)}
                  className={`
                    flex-shrink-0 px-4 py-2 rounded-full whitespace-nowrap
                    text-sm font-semibold transition-all duration-200 touch-feedback
                    ${isActive
                      ? 'bg-primary text-white shadow-warm'
                      : 'bg-secondary/40 text-foreground/80 hover:bg-secondary/70 hover:text-primary'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {category.name}
                  {category.itemCount > 0 && (
                    <span className={`ms-1.5 text-xs font-medium ${isActive ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {category.itemCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Edge fades — only when the row actually overflows, to signal "more this way" */}
        {overflowing && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent" />
          </>
        )}
      </div>
    </nav>
  )
}
